import { useEffect, useMemo } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import HomePage from '../HomePage';
import Header from '../Header';
import CreateQuoteForm from '../CreateQuoteForm';
import Resources from '../ResourceTab';
import QuotesPage from '../QuotesTab';
import PoliciesTab from '../PolicyTabs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs, Container, Typography } from '@mui/material';
import AdminPage from '../AdminPage';
import { endpoint } from '../..';
import { QuoteProvider } from '../../shared/contexts/QuoteContext';
import {
	clearQuoteId,
	resetStage,
} from '../../services/features/quoteSlice/quoteSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
	useLogoutMutation,
} from '../../services/features/authApi/authApi';
import { useGetBrokerByIdQuery } from '../../services/features/brokerApi/brokerApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';

/***
 * Component for rendering tabs and navigating between different sections of the application.
 ***/
const MenuTabs = () => {
	const params = useParams();

	const [logout, { isLoading }] = useLogoutMutation();

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const {
		id: brokerId,
		expiration: userExpiration,
		admin,
	} = useSelector((state: RootState) => state.authSlice);

	const { data: broker, ...brokerQuery } = useGetBrokerByIdQuery(brokerId ?? skipToken, {
		skip: !brokerId,
	});

	const brokerName = broker?.firstName ?? '';

	// const {
	// 	data: refreshTokenData,
	// 	isError,
	// 	isUninitialized,
	// 	refetch,
	// } = useGetRefreshTokenQuery(userExpiration, { skip: brokerId == null });

	// const expiration = useMemo(() => {
	// 	if (refreshTokenData?.expiration) {
	// 		const expirationDate = new Date(refreshTokenData.expiration);
	// 		return expirationDate.getTime();
	// 	}
	// }, [refreshTokenData]);

	// const refreshTokenInterval = getRefreshTokenInterval(currentEnv);

	// useEffect(() => {
	// 	if (isError && !isUninitialized) {
	// 		logout(undefined)
	// 			.unwrap()
	// 			.then((r) => console.log('Logged out user', r))
	// 			.catch((e) => console.log('Error logging out user', e));
	// 		return;
	// 	}
	// 	if (isUninitialized) {
	// 		return;
	// 	}

	// 	const checkTokenExpiry = () => {
	// 		const currentTime = new Date().getTime();
	// 		console.log('CHECK TOKEN', expiration ? expiration - currentTime <= 2 * 60000 : '');

	// 		// Refresh the token if it is about to expire
	// 		if (expiration && expiration - currentTime <= 2 * 60000) {
	// 			console.log('Token is about to expire', expiration);
	// 			refetch();
	// 		}
	// 	};
	// 	const intervalId = setInterval(checkTokenExpiry, 30000); // Check every 1 minute

	// 	return () => clearInterval(intervalId);
	// }, [expiration, isError, isUninitialized, refetch]);

	// useEffect(() => {
	// 	// TODO: Use setTimeout  instead of setInterval to  make refresh token requests stable.

	// 	// Set up a periodic token refresh interval
	// 	const interval = setInterval(async () => {
	// 		let res = await refreshToken();
	// 		console.log('Here is the res', res);

	// 		if (!res.token || res.token === '') {
	// 			await logout();
	// 		}
	// 		// Get the refresh token interval value based on the current environment
	// 	}, 500);//refreshTokenInterval);

	// 	return () => clearInterval(interval);
	// }, [logout]);

	useEffect(() => {
		if (brokerId == null) {
			navigate('/home');
		}
	}, [brokerId]);

	// Determine which component to render based on the route parameter (params?.tab)
	const renderContent = () => {
		// eslint-disable-next-line default-case
		switch (params?.tab) {
			case 'home':
				return (
					<div className='homePage'>
						<HomePage />
					</div>
				);
			case 'quotes':
				return (
					<div className='quotePage'>
						<QuotesPage />
					</div>
				);
			case 'policies':
				return (
					<div className='policyPage'>
						<PoliciesTab />
					</div>
				);
			case 'create-quote':
				return (
					<div className='createQuoteForm'>
						<QuoteProvider>
							<CreateQuoteForm />
						</QuoteProvider>
					</div>
				);
			case 'resources':
				return (
					<div className='resourcesPage'>
						<Resources />
					</div>
				);
			case 'faq':
				return (
					<div className='resourcesPage'>
						<Resources />
					</div>
				);
			case 'admin':
				return (
					<div className='adminPage'>
						<AdminPage />
					</div>
				);
		}
	};

	const tabs = [
		{ label: 'Home', link: '/home' },
		{ label: 'Quotes', link: '/quotes' },
		{ label: 'Policies', link: '/policies' },
		{ label: 'Create quote', link: '/create-quote' },
		{ label: 'FAQ', link: '/resources' },
		{ label: 'Admin', link: '/admin', admin: true },
	];

	const breadcrumbTabs: { [key: number]: string } = {
		0: 'Home',
		1: 'Quotes',
		2: 'Policies',
		3: 'Create quote',
		4: 'FAQ',
		5: 'Admin',
	};

	const breadcrumbText: { [key: number]: string } = useMemo(
		() => ({
			0: `Welcome back${brokerName.length > 0 ? `, ${brokerName}` : ''}`,
			1: 'Your quotes',
			2: 'Your policies',
			3: '',
			4: 'FAQ',
			5: 'Admin',
		}),
		[brokerName]
	);

	const handleTabClick = (tab: any) => {
		if (tab.label === 'Resources' || tab.label === 'FAQ') {
			// Open the Resources page in a new tab
			window.open(endpoint(tab.link), '_blank');
		} else if (tab.label === 'Create quote') {
			dispatch(clearQuoteId());
			dispatch(resetStage());
			navigate(tab.link, { state: { createQuote: true } }); // Navigate to create-quote page with parameters
		} else {
			navigate(tab.link);
		}
	};

	// Determine the active tab index based on the route
	const activeTab = tabs.findIndex((tab) => tab.link === `/${params?.tab || ''}`);
	const currentTabs = tabs.filter((tab) =>
		tab.admin && (!brokerId || admin !== true) ? false : true
	);

	// Return generic 404 page if the activeTab is wrong
	if (activeTab === -1) return <div className='homePage'>404</div>;

	return (
		<div>
			<Header />
			<Box
				width='100%'
				sx={{
					border: 1,
					borderColor: 'divider',
					backgroundColor: '#ffffff',
				}}
			>
				<Tabs value={activeTab > currentTabs.length - 1 ? 0 : activeTab}>
					{currentTabs.map((tab) => {
						return (
							<Tab
								key={tab.label}
								disableRipple
								label={tab.label}
								onClick={() => handleTabClick(tab)}
							/>
						);
					})}
				</Tabs>
			</Box>

			<Container sx={{ pt: 3 }}>
				<Breadcrumbs>
					{activeTab !== 0 && <Link to='/home'>Home</Link>}
					<Typography variant='subtitle1'>{breadcrumbTabs[activeTab]}</Typography>
				</Breadcrumbs>
				<Box pb='1em'>
					<Typography variant='pageHeader'>{breadcrumbText[activeTab]}</Typography>
				</Box>
				{renderContent()}
			</Container>
		</div>
	);
};

export default MenuTabs;